<template>
  <v-combobox
      :value="selection"
      :items="vehicles"
      item-text="registrationNumber"
      item-value="id"
      :label="label"
      :placeholder="placeholder"
      :disabled="disabled"
      :hint="hint"
      :persistent-hint="!!hint"
      :loading="loading"
      :filter="filterFn"
      return-object
      clearable
      hide-no-data
      :search-input.sync="searchInput"
      @input="onInput"
      @blur="onBlur"
  >
    <template #item="{item}">
      <v-list-item-content>
        <v-list-item-title>{{ item.registrationNumber }}</v-list-item-title>
        <v-list-item-subtitle v-if="vehicleSubtitle(item)">
          {{ vehicleSubtitle(item) }}
        </v-list-item-subtitle>
      </v-list-item-content>
    </template>
    <template #selection="{item}">
      <span>{{ item && typeof item === 'object' ? item.registrationNumber : item }}</span>
    </template>
  </v-combobox>
</template>

<script>
function normalizePlate(value) {
  if (!value) return ''
  return String(value).toUpperCase().replace(/[^A-Z0-9]/g, '')
}

/**
 * Plate input shared by every "assign a vehicle by plate" surface (badge edit,
 * badge popup). Pick an existing vehicle from the list OR type a new plate.
 *
 * v-model payload (object): { vehicleId } when an existing vehicle is chosen,
 * { registrationNumber } when free text is typed, {} when cleared. The backend
 * resolves the plate (lookup-first) when registrationNumber is sent.
 */
export default {
  name: 'VehiclePlateCombobox',

  model: { prop: 'value', event: 'change' },

  props: {
    value: { type: Object, default: () => ({}) },
    vehicles: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    label: { type: String, default: '' },
    placeholder: { type: String, default: '' },
  },

  data() {
    return { searchInput: null }
  },

  computed: {
    selection() {
      const v = this.value || {}
      if (v.vehicleId) {
        return this.vehicles.find(x => x.id === v.vehicleId) || null
      }
      if (typeof v.registrationNumber === 'string') {
        return v.registrationNumber
      }
      return null
    },

    hint() {
      const v = this.value || {}
      if (typeof v.registrationNumber === 'string' && v.registrationNumber.trim()) {
        const norm = normalizePlate(v.registrationNumber)
        const match = this.vehicles.find(x => normalizePlate(x.registrationNumber) === norm)
        if (!match) {
          return this.$t('member.vehicle-selector.will-create-hint')
        }
      }
      return ''
    },
  },

  methods: {
    vehicleSubtitle(item) {
      if (!item || typeof item !== 'object') return ''
      return [item.brand, item.model].filter(Boolean).join(' ')
    },

    filterFn(item, queryText) {
      const q = normalizePlate(queryText)
      if (!q) return true
      return normalizePlate(item.registrationNumber).includes(q)
    },

    onBlur() {
      const text = (this.searchInput || '').trim()
      if (!text) return
      const sel = this.selection
      const selText = sel && typeof sel === 'object' ? sel.registrationNumber : sel
      if (selText && normalizePlate(selText) === normalizePlate(text)) return
      this.onInput(text)
    },

    onInput(val) {
      if (val == null) {
        this.$emit('change', {})
        return
      }
      if (typeof val === 'object' && val.id) {
        this.$emit('change', { vehicleId: val.id })
        return
      }
      if (typeof val === 'string') {
        const trimmed = val.trim()
        if (!trimmed) {
          this.$emit('change', {})
          return
        }
        const norm = normalizePlate(trimmed)
        const match = this.vehicles.find(x => normalizePlate(x.registrationNumber) === norm)
        this.$emit('change', match ? { vehicleId: match.id } : { registrationNumber: trimmed })
      }
    },
  },
}
</script>
